import React, { useEffect, useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import ConversationAssistant from '@atlassian/conversation-assistant/AsyncConversationAssistant';
import { type UIConfig } from '@atlassian/conversation-assistant/types';

import { useAreLLMPoweredFeaturesEnabled } from '../../../controllers/entitlement-checks';

import { useFpsChatConfig } from './use-fps-chat-config';

/**
 * Source tag applied to the `chat-new` event so chat threads seeded from the
 * Full Page Search input can be distinguished in analytics and subscribe handlers.
 */
export const FPS_CHAT_SOURCE = 'fullPageSearch';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
		width: '100%',
		maxWidth: '70rem',
		height: '100%',
		marginRight: 'auto',
		marginLeft: 'auto',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
});

const FPS_CHAT_UI_CONFIG: Partial<UIConfig> = { elements: { mainChatHeader: false } };

export type FullPageChatProps = {
	/** Tenant cloud id (bootstrap `cloudId`). Maps to the config `siteId`. */
	cloudId: string;
	/** Current user id (bootstrap `user.id`). */
	userId: string | undefined | null;
	/** Host product key, e.g. 'confluence' | 'jira'. Defaults to 'confluence'. */
	product: string | undefined;
	/**
	 * The current search query. When non-empty on mount, it seeds a new chat thread
	 * (QF input → start a chat thread). Empty renders the widget's landing state.
	 */
	seedQuery?: string;
};

/**
 * Full Page Search "chat" surface — mounts the shared Rovo `ConversationAssistant`
 * widget in place of the search results body when `pageMode === 'chat'`.
 *
 * This component is presentational + self-contained: it receives all host context as
 * props (no react-sweet-state usage, keeping it compliant with the search-page-relay
 * boundary rule).
 *
 * Code-splitting: this module is intentionally lightweight — the only heavy dependency,
 * the Rovo chat widget, is imported via `@atlassian/conversation-assistant/AsyncConversationAssistant`,
 * which owns its OWN `React.lazy` / `react-loadable` boundary and dynamically imports the
 * widget chunk on mount. We therefore do NOT add a second `React.lazy` around this module:
 * doing so would create a chained lazy boundary (two sequential dynamic-import waterfalls),
 * violating the platform code-splitting rules. The heavy widget code still never enters
 * search-page's main chunk until `<FullPageChat/>` is actually mounted (which only happens
 * behind the `pageMode === 'chat'` + `rovo_search_chat_convergence` gate at the call-site).
 */
export const FullPageChat = ({
	cloudId,
	userId,
	product,
	seedQuery,
}: FullPageChatProps): React.JSX.Element | null => {
	const isLLMEnabled = useAreLLMPoweredFeaturesEnabled();
	const config = useFpsChatConfig({ cloudId, userId, product });
	const publish = usePublish('ai-mate');

	const hasSeededRef = useRef(false);
	useEffect(() => {
		if (hasSeededRef.current) {
			return;
		}
		const trimmedQuery = seedQuery?.trim();
		if (!isLLMEnabled || !trimmedQuery) {
			return;
		}
		hasSeededRef.current = true;
		publish({
			type: 'chat-new',
			source: FPS_CHAT_SOURCE,
			openChat: false,
			data: {
				prompt: trimmedQuery,
				dialogues: [],
				mode: { useCurrentPageContext: false },
			},
		});
	}, [isLLMEnabled, seedQuery, publish]);

	if (!isLLMEnabled || !fg('rovo_search_chat_convergence')) {
		return null;
	}

	return (
		<UFOSegment name="search-page-fps-chat">
			<Box xcss={styles.root}>
				<ConversationAssistant
					testId="fps-chat"
					config={config}
					isNav4Enabled
					uiConfig={FPS_CHAT_UI_CONFIG}
				/>
			</Box>
		</UFOSegment>
	);
};
